import { AnimatedSprite, Texture } from 'pixi.js';
import { FSM, Store, TextureFactory } from '../../core';
import { IEventEmitter, Point, StatefulAnimation, StateMap, Vector } from '../../interface';
import { BaseObject } from '..';
import { PatternBar } from '../../ui';
import AttackProgressBar from '../../ui/AttackProgressBar';
import TextureInfo from './Eggplant.json';
import { inQuint } from '../../util';
import { sound } from '@pixi/sound';

interface IMonster extends IEventEmitter {
  hp: number;
  score: number;
  velocity: Vector;
  position: Point;
}

class Eggplant extends BaseObject {
  public hp: number;
  public score: number;
  private fsm: FSM<IMonster>;
  private statefulAnimation: StatefulAnimation;
  private animatedSprite: AnimatedSprite;
  private targetObject?: BaseObject;
  private patternUI: PatternBar;
  private attackProgressBarUI: AttackProgressBar;

  private attackCasting: number;
  private attackCastingMax: number;
  private attackEasingDT: number;
  constructor() {
    super();
    const textures = TextureFactory.instance.load(TextureInfo);
    this.statefulAnimation = {
      IDLE: {
        textures: textures.slice(60, 79),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      WALK: {
        textures: textures.slice(60, 79),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      HIT: {
        textures: textures.slice(0, 19),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      DIE: {
        textures: textures.slice(20, 59),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      ATTACK: {
        textures: textures.slice(80, 93),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
    };

    this.animatedSprite = new AnimatedSprite([Texture.EMPTY]);
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.onComplete = () => {
      this.fsm.trigger('ANIMATION_DONE');
    };
    this.animatedSprite.scale.set(1);
    this.fsm = new FSM<IMonster>(this, this.states, 'IDLE');
    this.stage.addChild(this.animatedSprite);

    // Set UI...
    const patterns = new Array(4).fill(1).map(() => Math.floor(Math.random() * 5));
    this.hp = patterns.length;
    this.score = patterns.length * 5;
    this.attackEasingDT = 0;
    this.patternUI = new PatternBar(patterns);
    this.patternUI.scale.set(2);
    this.patternUI.y = -180;
    this.ui.addChild(this.patternUI);

    this.attackCasting = 0;
    this.attackCastingMax = 300;
    this.attackProgressBarUI = new AttackProgressBar(this.attackCasting, this.attackCastingMax);
    this.attackProgressBarUI.y = 20;
    this.attackProgressBarUI.scale.set(2);
    this.ui.addChild(this.attackProgressBarUI);

    this.on('symbol', (symbol: number) => {
      const destroyed = this.patternUI.destroySymbol(symbol);

      if (destroyed) {
        this.hit(1);
      }
    });
  }

  public get state(): string {
    return this.fsm.state || '';
  }

  public update(elapsed: number): void {
    super.update(elapsed);
    this.followingTarget();

    // Can't move
    if (['HIT', 'DIE'].includes(this.state || '')) {
      this.velocity.x = 0;
      this.velocity.y = 0;
    }

    // DIE??
    if (this.state === 'DIE') {
      if (this.container.alpha > 0.01 * elapsed) {
        this.container.alpha -= 0.01 * elapsed;
      } else {
        this.container.alpha = 0;
        this.emit('destroy');
      }
    }

    if (this.state === 'IDLE') {
      // Attack Casting...
      if (this.attackCasting < this.attackCastingMax - elapsed) {
        this.attackCasting += elapsed;
      } else {
        this.attackCasting = this.attackCastingMax;
      }

      if (this.attackCasting === this.attackCastingMax) {
        this.attackCasting = 0;
        this.attackEasingDT = 0;
        this.fsm.trigger('ATTACK');
      }
    }

    if (this.state === 'ATTACK' && this.targetObject) {
      const frames = 30;
      if (this.attackEasingDT < frames && this.attackEasingDT + elapsed >= frames) {
        this.emit('attack', 1); // damage
      }

      this.attackEasingDT += elapsed;
      if (this.attackEasingDT <= frames) {
        const value = inQuint(this.attackEasingDT / frames);
        const diffX = this.targetObject.position.x - this.position.x;
        const diffY = this.targetObject.position.y - this.position.y;

        this.stage.x = diffX * value;
        this.stage.y = diffY * value;
      } else if (this.attackEasingDT <= frames * 2) {
        const value = inQuint((frames * 2 - this.attackEasingDT) / frames);
        const diffX = this.targetObject.position.x - this.position.x;
        const diffY = this.targetObject.position.y - this.position.y;

        this.stage.x = diffX * value;
        this.stage.y = diffY * value;
      } else {
        this.attackEasingDT = 0;
        this.fsm.trigger('ANIMATION_DONE', 'ATTACK_DONE');
      }
    }

    this.fsm.update();
    this.patternUI.update(elapsed);
    this.attackProgressBarUI.update(this.attackCasting, this.attackCastingMax);
    this.updateZIndex();
  }

  public setTargetObject(targetObject: BaseObject): void {
    this.targetObject = targetObject;
  }

  private followingTarget(): void {
    if (this.targetObject) {
      const radius = Math.sqrt(
        (this.targetObject.position.x - this.position.x) ** 2 + (this.targetObject.position.y - this.position.y) ** 2
      );

      if (radius >= 300) {
        const radian = Math.atan2(
          this.targetObject.position.y - this.position.y,
          this.targetObject.position.x - this.position.x
        );
        const speed = 2;
        this.velocity.x = Math.cos(radian) * speed;
        this.velocity.y = Math.sin(radian) * speed;

        if (this.velocity.x < 0) {
          this.animatedSprite.scale.x = Math.abs(this.animatedSprite.scale.x) * -1;
          this.patternUI.scale.x = 2;
        } else if (this.velocity.x > 0) {
          this.animatedSprite.scale.x = Math.abs(this.animatedSprite.scale.x) * 1;
          this.patternUI.scale.x = -2;
        }
      } else {
        this.velocity.x = 0;
        this.velocity.y = 0;
      }
    }
  }

  public hit(damage: number): void {
    this.hp -= damage;
    this.attackEasingDT = 0;

    sound.play('/audio/knife.mp3', { loop: false, volume: Store.instance.state.volume as number });

    if (this.hp <= 0) {
      this.hp = 0;
      sound.play('/audio/vegetable_die.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.fsm.trigger('DIE');
    } else {
      this.fsm.trigger('HIT');
    }
    this.vibrator.vibrate(24);
    this.emit('damaged', damage);
  }

  private get states(): StateMap<IMonster> {
    return {
      IDLE: {
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        ATTACK: [{ events: ['ATTACK'] }],
        WALK: [[['abs', { refs: ['velocity', 'x'] }], '>', 0], '||', [['abs', { refs: ['velocity', 'y'] }], '>', 0]],
      },
      WALK: {
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        ATTACK: [{ events: ['ATTACK'] }],
        IDLE: [[{ refs: ['velocity', 'x'] }, '===', 0], '&&', [{ refs: ['velocity', 'y'] }, '===', 0]],
      },
      HIT: {
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      DIE: {},
      ATTACK: {
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        IDLE: [{ events: ['ANIMATION_DONE', 'ATTACK_DONE'] }],
      },
    };
  }

  public transition(prevState: string, nextState: string): void {
    const info = this.statefulAnimation[nextState];
    const flipX = this.animatedSprite.scale.x < 0 ? -1 : 1;

    this.animatedSprite.textures = info.textures;
    this.animatedSprite.x = info.offset.x * flipX;
    this.animatedSprite.y = info.offset.y;
    this.animatedSprite.scale.set(flipX * Math.abs(this.animatedSprite.scale.x), this.animatedSprite.scale.y);
    this.animatedSprite.animationSpeed = info.animationSpeed;
    this.animatedSprite.loop = info.loop;

    this.animatedSprite.gotoAndPlay(0);
  }
}

export default Eggplant;
